import { FC, useState } from "react"
import { generateUuid } from "@/helpers/uuid"
import {
  useDevDurabilityConsumeMutation,
  useDevGrantMutation,
  useDevMoveMutation,
  useDevRemoveMutation,
  useDevRepairMutation
} from "@/store/apis/inventoryAPI"
import styles from "./DebugPanel.module.scss"
import {
  DEBUG_ITEM_CODES,
  DEBUG_RARITY_CODES,
  DEBUG_STACKABLE_CODES
} from "./constants"
import { IDebugPanelProps } from "./types"

export const DebugPanel: FC<IDebugPanelProps> = ({ playerId, items }) => {
  const [grant] = useDevGrantMutation()
  const [remove] = useDevRemoveMutation()
  const [move] = useDevMoveMutation()
  const [durabilityConsume] = useDevDurabilityConsumeMutation()
  const [repair] = useDevRepairMutation()

  const [code, setCode] = useState<string>(DEBUG_ITEM_CODES[0])
  const [rarity, setRarity] = useState<string>(DEBUG_RARITY_CODES[0])
  const [qty, setQty] = useState(1)
  const [removeId, setRemoveId] = useState("")
  const [moveId, setMoveId] = useState("")
  const [toPlayerId, setToPlayerId] = useState("")
  const [durabilityId, setDurabilityId] = useState("")
  const [wearAmount, setWearAmount] = useState(10)

  // Non-stackable equipment must be granted qty 1 (maxStack 1) or the backend
  // denies silently. Selections are re-derived against the live `items` so a
  // remove/transfer never targets an instance that a prior op already refetched away.
  const stackable = DEBUG_STACKABLE_CODES.includes(code)
  const grantQty = stackable ? qty : 1
  const effectiveRemoveId = items.some((i) => i.id === removeId)
    ? removeId
    : (items[0]?.id ?? "")
  const effectiveMoveId = items.some((i) => i.id === moveId)
    ? moveId
    : (items[0]?.id ?? "")
  const equipment = items.filter((i) => i.maxDurability !== null)
  const effectiveDurabilityId = equipment.some((i) => i.id === durabilityId)
    ? durabilityId
    : (equipment[0]?.id ?? "")

  return (
    <div className={styles.DebugPanel}>
      <strong>DEBUG</strong>

      <div className={styles.DebugPanel_self}>
        <span>current player:</span>
        <input readOnly value={playerId} onFocus={(e) => e.target.select()} />
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText(playerId)}
        >
          Copy
        </button>
      </div>

      <fieldset>
        <legend>Grant</legend>
        <select value={code} onChange={(e) => setCode(e.target.value)}>
          {DEBUG_ITEM_CODES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={rarity} onChange={(e) => setRarity(e.target.value)}>
          {DEBUG_RARITY_CODES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          disabled={!stackable}
          value={grantQty}
          onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
        />
        <button
          type="button"
          onClick={() =>
            grant({
              playerId,
              definitionCode: code,
              quantity: grantQty,
              rarityCode: rarity,
              idempotencyKey: generateUuid()
            })
          }
        >
          Grant
        </button>
      </fieldset>

      <fieldset>
        <legend>Remove</legend>
        <select
          value={effectiveRemoveId}
          onChange={(e) => setRemoveId(e.target.value)}
        >
          {items.map((i) => (
            <option key={i.id} value={i.id}>
              {i.definitionCode} — {i.id}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!effectiveRemoveId}
          onClick={() => remove({ playerId, instanceId: effectiveRemoveId })}
        >
          Remove
        </button>
      </fieldset>

      <fieldset>
        <legend>Transfer</legend>
        <input
          type="text"
          placeholder="toPlayerId (UUID)"
          value={toPlayerId}
          onChange={(e) => setToPlayerId(e.target.value)}
        />
        <select
          value={effectiveMoveId}
          onChange={(e) => setMoveId(e.target.value)}
        >
          {items.map((i) => (
            <option key={i.id} value={i.id}>
              {i.definitionCode} — {i.id}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!toPlayerId || !effectiveMoveId}
          onClick={() =>
            move({
              fromPlayerId: playerId,
              toPlayerId,
              instanceId: effectiveMoveId
            })
          }
        >
          Transfer
        </button>
      </fieldset>

      <fieldset>
        <legend>Durability</legend>
        <select
          value={effectiveDurabilityId}
          onChange={(e) => setDurabilityId(e.target.value)}
        >
          {equipment.length === 0 && <option value="">— no equipment —</option>}
          {equipment.map((i) => (
            <option key={i.id} value={i.id}>
              {i.definitionCode} — {i.durability}/{i.maxDurability} —{" "}
              {i.id.slice(0, 8)}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          step={1}
          value={wearAmount}
          onChange={(e) => setWearAmount(Number(e.target.value))}
        />
        <button
          type="button"
          disabled={
            !effectiveDurabilityId ||
            !Number.isInteger(wearAmount) ||
            wearAmount < 1
          }
          onClick={() =>
            durabilityConsume({
              playerId,
              instanceId: effectiveDurabilityId,
              amount: wearAmount
            })
          }
        >
          Wear
        </button>
        <button
          type="button"
          disabled={!effectiveDurabilityId}
          onClick={() =>
            repair({ playerId, instanceId: effectiveDurabilityId })
          }
        >
          Repair
        </button>
      </fieldset>
    </div>
  )
}
